import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TurnId } from '../types/conversation';

interface UseActiveMessageOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  messageRefs: Map<TurnId, React.RefObject<HTMLDivElement | null>>;
}

interface MessageScrollRange {
  messageId: TurnId;
  scrollStart: number; // Scroll position where this message starts being highlighted
  scrollEnd: number; // Scroll position where this message stops being highlighted
}

/**
 * Hook to track which user message bubble should be highlighted in the navigator
 * Uses scroll-based position mapping for smooth, inertia-free highlighting
 *
 * Core idea: Pre-calculate scroll ranges for each message, then use binary search
 * to find which message corresponds to current scroll position.
 *
 * Returns a Set containing the currently highlighted message ID
 */
export const useActiveMessage = ({ containerRef, messageRefs }: UseActiveMessageOptions) => {
  const [highlightedMessageId, setHighlightedMessageId] = useState<TurnId | null>(null);
  const rangesRef = useRef<MessageScrollRange[]>([]);
  const interactionFocusTimerRef = useRef<NodeJS.Timeout | null>(null);
  const interactionFocusRef = useRef<TurnId | null>(null);
  const lastScrollTopRef = useRef<number>(-1); // Track scroll to detect if user is actively scrolling

  /**
   * Pre-calculate scroll ranges for each message
   * Each message's range is from the midpoint before it to the midpoint after it
   * This ensures smooth transitions and eliminates "dark gaps"
   */
  const calculateScrollRanges = useCallback((): MessageScrollRange[] => {
    const container = containerRef.current;
    if (!container || messageRefs.size === 0) return [];

    const scrollTop = container.scrollTop;
    const viewportHeight = container.clientHeight;
    const scrollHeight = container.scrollHeight;
    const maxScroll = Math.max(0, scrollHeight - viewportHeight);

    // Get all message IDs in order
    const messageIds = Array.from(messageRefs.keys());

    if (messageIds.length === 0) return [];

    // Calculate position for each message
    const messagePositions: Array<{ messageId: TurnId; position: number }> = [];

    for (const messageId of messageIds) {
      const ref = messageRefs.get(messageId);
      if (!ref?.current) continue;

      const userMessageElement = ref.current.querySelector('[data-testid="turn-user"]');
      if (!userMessageElement) continue;

      const rect = userMessageElement.getBoundingClientRect();
      const elementTop = rect.top + scrollTop;
      const elementCenter = elementTop + rect.height / 2;

      messagePositions.push({ messageId, position: elementCenter });
    }

    if (messagePositions.length === 0) return [];

    const ranges: MessageScrollRange[] = [];

    // Calculate ranges: each message covers from previous midpoint to next midpoint
    for (let i = 0; i < messagePositions.length; i++) {
      const current = messagePositions[i];
      const prev = messagePositions[i - 1];
      const next = messagePositions[i + 1];

      let scrollStart = 0;
      let scrollEnd = maxScroll;

      // Start from midpoint with previous message
      if (prev) {
        const midpoint = (prev.position + current.position) / 2;
        scrollStart = Math.floor(midpoint - viewportHeight / 2);
        scrollStart = Math.max(0, scrollStart);
      } else {
        // First message: start from 0
        scrollStart = 0;
      }

      // End at midpoint with next message
      if (next) {
        const midpoint = (current.position + next.position) / 2;
        scrollEnd = Math.ceil(midpoint - viewportHeight / 2);
        scrollEnd = Math.min(maxScroll, scrollEnd);
      } else {
        // Last message: end at max scroll
        scrollEnd = maxScroll;
      }

      // Ensure ranges don't have gaps
      if (i > 0 && ranges.length > 0) {
        const prevRange = ranges[ranges.length - 1];
        if (scrollStart > prevRange.scrollEnd) {
          // Small gap, extend previous range
          prevRange.scrollEnd = scrollStart;
        } else if (scrollStart < prevRange.scrollEnd) {
          // Overlap, adjust to avoid overlap
          scrollStart = prevRange.scrollEnd;
        }
      }

      ranges.push({ messageId: current.messageId, scrollStart, scrollEnd });
    }

    return ranges;
  }, [containerRef, messageRefs]);

  /**
   * Find which message should be highlighted based on current scroll position
   * Uses binary search for O(log n) performance
   */
  const findMessageByScroll = useCallback((scrollTop: number): TurnId | null => {
    const ranges = rangesRef.current;
    if (ranges.length === 0) return null;

    // Handle edge case: scrollTop = 0 should return first message
    if (scrollTop === 0 && ranges.length > 0) {
      return ranges[0].messageId;
    }

    // Binary search for the range containing scrollTop
    let left = 0;
    let right = ranges.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const range = ranges[mid];

      if (scrollTop >= range.scrollStart && scrollTop <= range.scrollEnd) {
        return range.messageId;
      } else if (scrollTop < range.scrollStart) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    // If not found in any range, return the closest one
    if (left < ranges.length) {
      return ranges[left].messageId;
    }
    if (right >= 0) {
      return ranges[right].messageId;
    }

    return null;
  }, []);

  /**
   * Update the highlighted message based on interactionFocus and scroll position
   */
  const updateHighlight = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;

    // If there's an active interaction focus, use it
    if (interactionFocusRef.current) {
      setHighlightedMessageId(interactionFocusRef.current);
      return;
    }

    // Otherwise use scroll-based mapping
    const messageFromScroll = findMessageByScroll(scrollTop);
    setHighlightedMessageId(messageFromScroll);
  }, [containerRef, findMessageByScroll]);

  /**
   * Set interaction focus (called when user clicks a message)
   * Clears after 1.5 seconds, returning control to scroll-based highlighting
   */
  const setInteractionFocus = useCallback(
    (messageId: TurnId) => {
      // Clear existing timer
      if (interactionFocusTimerRef.current) {
        clearTimeout(interactionFocusTimerRef.current);
      }

      interactionFocusRef.current = messageId;
      lastScrollTopRef.current = -1; // Reset scroll tracking
      updateHighlight();

      // Auto-clear after 1.5 seconds
      interactionFocusTimerRef.current = setTimeout(() => {
        interactionFocusRef.current = null;
        updateHighlight();
      }, 1500);
    },
    [updateHighlight]
  );

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const currentScrollTop = container.scrollTop;

    // If there's an interaction focus and user is actively scrolling, release it immediately
    if (interactionFocusRef.current && lastScrollTopRef.current >= 0) {
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTopRef.current);
      // If user scrolled more than 50px, release the lock immediately
      if (scrollDelta > 50) {
        interactionFocusRef.current = null;
        if (interactionFocusTimerRef.current) {
          clearTimeout(interactionFocusTimerRef.current);
          interactionFocusTimerRef.current = null;
        }
      }
    }

    lastScrollTopRef.current = currentScrollTop;

    // Update highlight based on new scroll position
    requestAnimationFrame(() => {
      updateHighlight();
    });
  }, [containerRef, updateHighlight]);

  // Recalculate ranges when messages change
  useEffect(() => {
    const ranges = calculateScrollRanges();
    rangesRef.current = ranges;

    // Update highlight immediately with new ranges
    updateHighlight();
  }, [calculateScrollRanges, updateHighlight]);

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial calculation
    const ranges = calculateScrollRanges();
    rangesRef.current = ranges;
    updateHighlight();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (interactionFocusTimerRef.current) {
        clearTimeout(interactionFocusTimerRef.current);
      }
    };
  }, [containerRef, handleScroll, calculateScrollRanges, updateHighlight]);

  // Return a Set for compatibility with existing code
  // Also expose setInteractionFocus for click handling
  const result = useMemo(() => {
    const messageSet = highlightedMessageId ? new Set([highlightedMessageId]) : new Set<TurnId>();
    return Object.assign(messageSet, {
      setInteractionFocus,
    });
  }, [highlightedMessageId, setInteractionFocus]);

  return result;
};
